import { useCallback, useEffect, useState } from "react";
import { subscriptionsService } from "../../features/subscriptions/service";
import type { AdminPlanCredits } from "../../features/subscriptions/types";
import ConfirmModal from "../../components/admin/ConfirmModal";
import { parseApiError } from "../../lib/http/apiErrors";
import { formatISTMonthDayYear } from "../../utils/dateUtils";
import { useAdminToast } from "../../context/AdminToastContext";

const STATUS_STYLE: Record<string, string> = {
  credited: "bg-emerald-500/10 text-emerald-400",
  pending: "bg-amber-500/10 text-amber-400",
  closed: "bg-gray-700/40 text-gray-400",
};

/** One user's subscription plan credits (TM-1836): whether they are on plan
 *  credits, what is left this month, every monthly grant, and removing what
 *  is left after a refund. */
export default function AdminPlanCreditsPanel({ userId }: { userId: string }) {
  const { showError, showSuccess } = useAdminToast();
  const [data, setData] = useState<AdminPlanCredits | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await subscriptionsService.adminGetPlanCredits(userId));
    } catch {
      setData(null); // older backend or no access: the panel just hides
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  if (!data) return null;
  const open = data.grants.some((g) => g.status !== "closed" && new Date(g.window_end) > new Date());

  const removeLeft = async () => {
    setBusy(true);
    try {
      const res = await subscriptionsService.adminClosePlanCredits(userId, "refund");
      showSuccess(res.closed ? "This month's remaining plan credits were removed." : "Nothing to remove.");
      setConfirm(false);
      await load();
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h2 className="text-sm font-semibold text-white">Plan Credits</h2>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${data.credit_mode ? "bg-emerald-500/10 text-emerald-400" : "bg-gray-700/40 text-gray-400"}`}>
          {data.credit_mode ? "On plan credits" : data.switch_on ? "On monthly counts" : "Plan credits switched off"}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        {data.credit_mode
          ? "Every plan action this user takes is paid from these credits first."
          : data.switch_on
            ? "No plan-credit month covers today: free plan, no paid period, or a period that began before plan credits were switched on (moves over at renewal)."
            : "Turn on Plan credits on Plans & Prices to use them."}
      </p>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-gray-800/60 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Plan credits left</p>
          <p className="text-lg font-bold text-white">{(data.plan_credits_left ?? 0).toLocaleString("en-IN")}</p>
        </div>
        <div className="rounded-xl bg-gray-800/60 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Expire on</p>
          <p className="text-lg font-bold text-white">{formatISTMonthDayYear(data.plan_credits_expire_at)}</p>
        </div>
        <div className="rounded-xl bg-gray-800/60 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Whole wallet</p>
          <p className="text-lg font-bold text-white">{data.balance_credits.toLocaleString("en-IN")}</p>
        </div>
      </div>

      {data.grants.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-1.5 pr-3 font-medium">Month</th>
                <th className="py-1.5 pr-3 font-medium">Plan</th>
                <th className="py-1.5 pr-3 font-medium text-right">Credits</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.grants.map((g) => (
                <tr key={g.key} className="border-t border-gray-800 text-gray-300">
                  <td className="py-1.5 pr-3 whitespace-nowrap">
                    {formatISTMonthDayYear(g.window_start)} – {formatISTMonthDayYear(g.window_end)}
                  </td>
                  <td className="py-1.5 pr-3 capitalize">{g.tier}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{g.credits.toLocaleString("en-IN")}</td>
                  <td className="py-1.5 pr-3">
                    <span className={`rounded px-1.5 py-0.5 ${STATUS_STYLE[g.status] ?? ""}`}>{g.status}</span>
                    {g.close_reason && <span className="ml-2 text-gray-500">{g.close_reason}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-gray-500">No plan credits granted yet.</p>
      )}

      {open && (
        <div className="mt-4">
          <button
            onClick={() => setConfirm(true)}
            className="px-4 py-2 bg-red-900/60 hover:bg-red-800 rounded-xl text-xs font-medium text-red-200"
          >
            Remove this month&apos;s plan credits (refund)
          </button>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title="Remove this month's plan credits?"
          description="Use this after refunding the subscription payment. What is left of this month's plan credits is taken out of the wallet now, and this month is not granted again even if the subscription stays active. Credits already spent and top-ups are not touched."
          confirmLabel="Remove plan credits"
          confirmClass="bg-red-600 hover:bg-red-500"
          irreversible
          busy={busy}
          busyLabel="Removing…"
          onConfirm={() => void removeLeft()}
          onCancel={() => setConfirm(false)}
        />
      )}
    </div>
  );
}
